import type { ClubMemberRole } from '../../api';
import type { User } from '../../types';
import { canHaveOwnershipShare, isGeneralUser } from '../../utils/isGeneralUser';
import { DateFormat } from '../../utils/DateFormat';
import {
  canManageMember,
  OWNER_STATUS_OPTIONS,
  roleLabel,
  todayIso,
} from './membersUtils';

type RoleOption = { value: ClubMemberRole; label: string };

type Props = {
  member: User;
  currentUserId: number | null;
  actorClubRole: string | null | undefined;
  busyId: number | null;
  joinedDrafts: Record<number, string>;
  roleOptions: RoleOption[];
  onOwnerPilotToggle: (nextIsPilot: boolean) => void;
  onRoleChange: (role: ClubMemberRole) => void;
  onJoinedFocus: () => void;
  onJoinedChange: (value: string) => void;
  onJoinedBlur: (value: string) => void;
  onActivatePending: () => void;
  onRestore: () => void;
  onRemove: () => void;
  onOpenCabinet: () => void;
};

export default function MemberCard({
  member: u,
  currentUserId,
  actorClubRole,
  busyId,
  joinedDrafts,
  roleOptions,
  onOwnerPilotToggle,
  onRoleChange,
  onJoinedFocus,
  onJoinedChange,
  onJoinedBlur,
  onActivatePending,
  onRestore,
  onRemove,
  onOpenCabinet,
}: Props) {
  const inactive = u.role === 'inactive';
  const pending = u.role === 'pending';
  const isSelf = u.id === currentUserId;
  const allowManage = canManageMember(actorClubRole, u, currentUserId);
  const manageTitle = isSelf
    ? 'Нельзя изменить себя'
    : u.role === 'owner'
      ? 'Владельца нельзя изменить'
      : (u.role === 'admin' ||
            (pending && u.pending_role === 'admin')) &&
          actorClubRole !== 'owner'
        ? 'Только владелец может изменить админа'
        : undefined;
  const canRestore =
    inactive && (actorClubRole === 'owner' || actorClubRole === 'admin');
  const canActivatePending =
    pending && (actorClubRole === 'owner' || actorClubRole === 'admin');
  const canEditRole =
    !inactive && !pending && allowManage && u.role !== 'owner';
  const canRemove = !inactive && allowManage;
  const canEditJoinedOn =
    (actorClubRole === 'owner' || actorClubRole === 'admin') && !pending;
  const canOpenPilotCabinet =
    !isGeneralUser(u) &&
    (actorClubRole === 'owner' ||
      ((actorClubRole === 'admin' || actorClubRole === 'moderator') &&
        u.role === 'pilot' &&
        !inactive &&
        !pending));

  return (
    <article
      className={[
        'members-card',
        inactive ? 'members-card-inactive' : '',
        pending ? 'members-card-pending' : '',
        canHaveOwnershipShare(u.role, u.is_pilot)
          ? 'members-card-owner'
          : 'members-card-other',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="members-card-header">
        <div
          className={[
            'members-card-identity',
            canOpenPilotCabinet ? 'members-card-identity-clickable' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          role={canOpenPilotCabinet ? 'link' : undefined}
          tabIndex={canOpenPilotCabinet ? 0 : undefined}
          title={
            canOpenPilotCabinet
              ? 'Открыть личный кабинет пилота'
              : undefined
          }
          onClick={canOpenPilotCabinet ? onOpenCabinet : undefined}
          onKeyDown={
            canOpenPilotCabinet
              ? (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  onOpenCabinet();
                }
              : undefined
          }
        >
          <div className="members-card-name">{u.name}</div>
          {u.login ? (
            <div className="members-card-login">@{u.login}</div>
          ) : null}
        </div>
        {u.role === 'owner' && isSelf ? (
          <div
            className="members-role-picker"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="members-role-picker-label" aria-hidden>
              Владелец клуба
            </span>
            <select
              className="members-role-select members-role-picker-select"
              value={u.is_pilot !== false ? '1' : '0'}
              disabled={busyId === u.id}
              aria-label={`Статус владельца ${u.name}`}
              onChange={(e) => {
                e.stopPropagation();
                const nextIsPilot = e.target.value === '1';
                if ((u.is_pilot !== false) === nextIsPilot) return;
                onOwnerPilotToggle(nextIsPilot);
              }}
            >
              {OWNER_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : canEditRole ? (
          <div
            className="members-role-picker"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="members-role-picker-label" aria-hidden>
              {roleLabel(u.role, u.pending_role)}
            </span>
            <select
              className="members-role-select members-role-picker-select"
              value={
                (['pilot', 'admin', 'moderator', 'technician', 'guest'].includes(
                  u.role || ''
                )
                  ? u.role
                  : 'pilot') as ClubMemberRole
              }
              disabled={busyId === u.id}
              aria-label={`Роль ${u.name}`}
              onChange={(e) => {
                e.stopPropagation();
                onRoleChange(e.target.value as ClubMemberRole);
              }}
            >
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="members-card-role" title={manageTitle}>
            {roleLabel(u.role, u.pending_role)}
          </div>
        )}
      </div>
      {canHaveOwnershipShare(u.role, u.is_pilot) ? (
        <div className="members-card-meta members-card-joined">
          <span className="members-card-meta-label">Собственник с</span>
          {canEditJoinedOn ? (
            <input
              className={[
                'members-joined-input',
                u.joined_on ? '' : 'members-joined-input-empty',
              ]
                .filter(Boolean)
                .join(' ')}
              type="date"
              value={
                joinedDrafts[u.id] !== undefined
                  ? joinedDrafts[u.id]
                  : u.joined_on || ''
              }
              max={todayIso()}
              disabled={busyId === u.id}
              aria-label={`Собственник с ${u.name}`}
              title={
                u.joined_on
                  ? undefined
                  : 'Не задана — в долях ТО с начала истории'
              }
              onFocus={onJoinedFocus}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onJoinedChange(e.target.value)}
              onBlur={(e) => {
                onJoinedBlur(e.currentTarget.value);
              }}
            />
          ) : (
            <span className="members-card-meta-value">
              {u.joined_on ? DateFormat(u.joined_on) : 'с основания'}
            </span>
          )}
        </div>
      ) : null}
      {canHaveOwnershipShare(u.role, u.is_pilot) &&
      canEditJoinedOn &&
      !u.joined_on ? (
        <p className="members-joined-hint">
          Нет даты — в долях ТО с начала истории
        </p>
      ) : null}
      {canRestore || canRemove || canActivatePending ? (
        <div
          className="members-card-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {canActivatePending ? (
            <button
              type="button"
              className="btn-member-action"
              disabled={busyId === u.id}
              onClick={onActivatePending}
            >
              Активировать
            </button>
          ) : null}
          {canRestore ? (
            <button
              type="button"
              className="btn-member-action"
              disabled={busyId === u.id}
              onClick={onRestore}
            >
              Восстановить
            </button>
          ) : null}
          {canRemove ? (
            <button
              type="button"
              className="btn-member-action btn-member-remove"
              disabled={busyId === u.id}
              title={manageTitle || 'Деактивировать'}
              onClick={onRemove}
            >
              Удалить
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
